import { GaugeSeries } from '@/components';
import { BarChartData, SortOption } from '@/core';

/**
 * Finds the column count that maximizes the per-gauge size within the given
 * panel dimensions.  Tries every possible column count from 1..count and picks
 * the one where `min(cellWidth, cellHeight)` is largest.
 */
export function calculateOptimalColumns(
  count: number,
  panelWidth: number,
  panelHeight: number,
  padding: number,
  minWidth: number
): number {
  if (count <= 1 || panelWidth <= 0 || panelHeight <= 0) return 1;

  let bestColumns = 1;
  let bestSize = 0;

  let bestEmptyCells = count;

  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const w = panelWidth / cols - padding;
    const h = panelHeight / rows - padding;
    if (w < minWidth) continue;
    const size = Math.min(w, h);
    const emptyCells = cols * rows - count;

    if (size > bestSize || (size === bestSize && emptyCells <= bestEmptyCells)) {
      bestSize = size;
      bestColumns = cols;
      bestEmptyCells = emptyCells;
    }
  }

  return bestColumns;
}

export function sortSeriesData(data: BarChartData[], sortOrder?: SortOption): BarChartData[];
export function sortSeriesData(data: GaugeSeries[], sortOrder?: SortOption): GaugeSeries[];
export function sortSeriesData(
  data: BarChartData[] | GaugeSeries[],
  sortOrder: SortOption = 'default'
): BarChartData[] | GaugeSeries[] {
  if (sortOrder === 'desc') {
    // sort in ascending order by value
    data.sort((a, b) => {
      if (a.value === null || a.value === undefined) {
        return 1;
      }
      if (b.value === null || b.value === undefined) {
        return -1;
      }
      if (a.value === b.value) {
        return 0;
      }
      return a.value < b.value ? 1 : -1;
    });
  } else if (sortOrder === 'asc') {
    // sort in descending order by value
    data.sort((a, b) => {
      if (a.value === null || a.value === undefined) {
        return -1;
      }
      if (b.value === null || b.value === undefined) {
        return 1;
      }
      if (a.value === b.value) {
        return 0;
      }
      return a.value < b.value ? -1 : 1;
    });
  } else if (sortOrder === 'series-name-asc') {
    data.sort((a, b) => {
      return a.label.localeCompare(b.label);
    });
  } else if (sortOrder === 'series-name-desc') {
    data.sort((a, b) => b.label.localeCompare(a.label));
  }
  return data;
}
